import React, { useEffect, useMemo } from 'react';
import { Box, IconButton, Link, SxProps } from '@mui/material';
import { ClosingMethod as ClosingMethodType } from 'services/campaign/types';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { FormValues } from 'pages/CampaignCreation/types';
import { TextField, Dropdown, RadioGroup } from 'common/form';
import { useOrganization } from 'core/stores/hooks/useOrganization';
import { Paths } from 'services/navigation/paths';
import { Refresh } from '@mui/icons-material';
import { Loading } from 'components/Loading';
import { useListActiveMeetingTemplates } from 'modules/calendar/api/useListActiveMeetingTemplates';
import { getTemplateEditBlockedReason } from 'modules/calendar/utils/getTemplateEditBlockedReason';
import { formatMeetingDuration } from 'utils/fomatting';
import { SearchCalendarMeetingsItem } from 'modules/calendar/types/SearchCalendarMeetingsItem';
import { getDefaultCustomRequest } from 'modules/campaign-creation/utils/getDefaultCustomRequest';
import { useOrganizationConfig } from 'core/stores/hooks/useOrganizationConfig';

const iconContainer: SxProps = {
  width: 50
};

const dropdownRowSx: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  gap: '16px',
  alignItems: 'center'
};

const formatOrganizerAndParticipants = (
  organizerName: string,
  participantNames: string[]
): string => {
  if (participantNames.length > 0) {
    return `${organizerName}, ${participantNames.join(', ')}`;
  }
  return organizerName;
};

const createMeetingTemplateExtraData = (
  item: SearchCalendarMeetingsItem
): string[] => {
  const extraData: string[] = [];

  if (item?.organizerUserCalendar?.name) {
    const participantNames =
      item?.participants?.items
        ?.filter((p) => p?.userCalendar?.id !== item.organizerUserCalendarId)
        ?.map((p) => p?.userCalendar?.name)
        ?.filter((name): name is string => !!name) || [];

    extraData.push(
      formatOrganizerAndParticipants(
        item.organizerUserCalendar.name,
        participantNames
      )
    );
  }

  if (item?.durationMinutes) {
    extraData.push(formatMeetingDuration(item.durationMinutes));
  }

  if (item?.isVideoConference) {
    extraData.push('Video/phone');
  } else if (item?.location) {
    extraData.push(`In person, ${item.location}`);
  }

  return extraData;
};

type Props = {
  formValues: FormValues;
  isEdit: boolean | undefined;
  errors: FormikErrors<FormValues>;
  touched: FormikTouched<FormValues>;
  handleBlur: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  setFormValues: FormikProps<FormValues>['setValues'];
  onChangeField: (field: string, value: any, shouldValidate?: boolean) => void;
};

export const ClosingMethod: React.FC<Props> = ({
  formValues,
  errors,
  touched,
  handleBlur,
  setFormValues,
  onChangeField
}) => {
  const organization = useOrganization();
  const hasCalendarIntegration = organization?.hasCalendarIntegration;
  const {
    data: calendarMeetingTemplateOptions,
    loading,
    refetch
  } = useListActiveMeetingTemplates();
  const organizationConfig = useOrganizationConfig();

  useEffect(() => {
    if (!hasCalendarIntegration) {
      setFormValues((prev) => ({
        ...prev,
        closingMethod: ClosingMethodType.CUSTOM
      }));
    }
  }, [hasCalendarIntegration]);

  const calendarMeetingTemplateIdError = touched?.calendarMeetingTemplateId
    ? errors?.calendarMeetingTemplateId
    : '';

  const customRequestError = touched?.customRequest
    ? errors?.customRequest
    : '';

  const closingMethodError = touched?.closingMethod
    ? errors?.closingMethod
    : '';

  const meetingTemplateOptions = useMemo(() => {
    return calendarMeetingTemplateOptions
      .filter(
        (item) =>
          // Keep what the campaign already points at, or it looks cleared.
          item?.id === formValues.calendarMeetingTemplateId ||
          // Same rule as the edit block: a template the recruiter can't open
          // shouldn't be attachable to a new campaign.
          !getTemplateEditBlockedReason(item)
      )
      .map((item) => ({
        label: item?.title,
        value: item?.id,
        extraData: createMeetingTemplateExtraData(item)
      }));
  }, [calendarMeetingTemplateOptions, formValues.calendarMeetingTemplateId]);

  return (
    <>
      {hasCalendarIntegration && (
        <RadioGroup
          field={{
            options: [
              {
                label: (
                  <>
                    Ask candidates to schedule a meeting{' '}
                    <Link href={Paths.HOME_CALENDAR_MEETINGS} target="_blank">
                      Manage meetings
                    </Link>
                  </>
                ),
                value: ClosingMethodType.CALENDAR_MEETING_INTEGRATION
              },
              {
                label: 'Send message',
                value: ClosingMethodType.CUSTOM
              }
            ]
          }}
          onChange={(value) => {
            if (value === ClosingMethodType.CALENDAR_MEETING_INTEGRATION) {
              setFormValues((prev) => ({
                ...prev,
                customRequest: undefined,
                closingMethod: value
              }));
            }

            if (value === ClosingMethodType.CUSTOM) {
              setFormValues((prev) => ({
                ...prev,
                calendarMeetingTemplateId: undefined,
                customRequest: getDefaultCustomRequest(organizationConfig),
                closingMethod: value
              }));
            }
          }}
          value={formValues.closingMethod}
          errorMessage={closingMethodError}
        />
      )}

      {formValues.closingMethod ===
        ClosingMethodType.CALENDAR_MEETING_INTEGRATION && (
        <Box sx={dropdownRowSx}>
          <Dropdown
            name="calendarMeetingTemplateId"
            onChange={(value) =>
              onChangeField('calendarMeetingTemplateId', value)
            }
            value={formValues.calendarMeetingTemplateId}
            field={{
              options: meetingTemplateOptions,
              isSearchable: true,
              buttonText: 'Choose meeting type'
            }}
            errorMessage={calendarMeetingTemplateIdError}
            onBlur={handleBlur}
          />
          <Box sx={iconContainer}>
            {loading ? (
              <Loading size={20} />
            ) : (
              <IconButton
                disabled={loading}
                onClick={() => refetch()}
                color="primary"
              >
                <Refresh />
              </IconButton>
            )}
          </Box>
        </Box>
      )}
      {formValues.closingMethod === ClosingMethodType.CUSTOM && (
        <TextField
          name="customRequest"
          onChange={(value) => onChangeField('customRequest', value)}
          value={formValues.customRequest}
          field={{
            type: 'textarea',
            label: 'Write message',
            rows: 5
          }}
          errorMessage={customRequestError}
          onBlur={handleBlur}
        />
      )}
    </>
  );
};
